import { useQueries } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { FirstSteps } from '../components/FirstSteps.js';
import { flagOf, regionName } from '../components/geo.js';
import {
  HealthTrend,
  PulseStrip,
  ScoreRing,
  SeverityBar,
  UptimeStrip,
  hoursUptime,
} from '../components/SiteCharts.js';
import { GradePill, isGrade, useLocale } from '../components/site.js';
import {
  Button,
  LinkButton,
  Notice,
  Segmented,
  SelectField,
  StatusBadge,
  TableBox,
  TextField,
  type MonitorState,
  type Severity,
} from '../components/ui.js';
import { formatAgo, formatDateTime, formatNumber, formatTime, sameDay } from '../i18n.js';
import { useMe } from '../session.js';
import { useSites } from '../sites.js';

export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

type Site = Schemas['SiteSummary'];
type Filter = 'all' | 'problems' | 'down';
type Sort = 'priority' | 'name' | 'score' | 'problems';
type View = 'cards' | 'list';

const VIEW_KEY = 'wg.sites.view';

/** Cards or a compact list (T-79); remembered per browser, cards by default. */
function useView() {
  const [view, setView] = useState<View>(() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const choose = (next: View) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // not remembered: the choice holds until the page is left
    }
  };
  return [view, choose] as const;
}

const urgent = (s: Site) => s.openFindings.critical + s.openFindings.high;
const openTotal = (s: Site) => SEVERITIES.reduce((n, sev) => n + s.openFindings[sev], 0);
const isDown = (s: Site) => s.status !== 'paused' && s.uptimeState === 'down';
const stateOf = (s: Site): MonitorState => (s.status === 'paused' ? 'paused' : s.uptimeState);

/** Down first, then the most urgent problems, then the lowest score; paused at the end. */
const SORTS: Record<Sort, (a: Site, b: Site) => number> = {
  priority: (a, b) =>
    Number(a.status === 'paused') - Number(b.status === 'paused') ||
    Number(isDown(b)) - Number(isDown(a)) ||
    urgent(b) - urgent(a) ||
    (a.healthScore ?? 101) - (b.healthScore ?? 101) ||
    a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  score: (a, b) => (a.healthScore ?? 101) - (b.healthScore ?? 101),
  problems: (a, b) => openTotal(b) - openTotal(a) || urgent(b) - urgent(a),
};

/** Thresholds of the warning colour on a card and in the list (as before T-81). */
const SSL_WARN_DAYS = 14;
const SLOW_MS = 1500;
const DOMAIN_WARN_DAYS = 30;

/**
 * Übersicht (design/pages/app/overview.html, SP-UX10.2b): first steps, the sites that are down, the
 * sites as cards or a list with search, filter and sort, and the situation beside them (T-55a).
 */
export function Overview() {
  const { t } = useTranslation();
  const sites = useSites();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('priority');
  const [view, setView] = useView();
  const items = sites.data?.items;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...(items ?? [])]
      .filter((s) => !q || s.name.toLowerCase().includes(q) || s.host.toLowerCase().includes(q))
      .filter((s) =>
        filter === 'down' ? isDown(s) : filter === 'problems' ? openTotal(s) > 0 : true,
      )
      .sort(SORTS[sort]);
  }, [items, query, filter, sort]);

  let content: ReactNode;
  if (sites.isPending)
    content = (
      <div className="sites" role="status">
        <span className="sr-only">{t('app.loading')}</span>
        {[0, 1].map((n) => (
          <div key={n} className="site" aria-hidden="true">
            <div className="skel w-1/2" />
            <div className="skel w-1/3" />
            <div className="skel h-2" />
            <div className="skel h-9 w-2/5" />
            <div className="skel" />
            <div className="skel w-2/3" />
          </div>
        ))}
      </div>
    );
  else if (sites.isError)
    content = (
      <Notice
        kind="error"
        title={t('overview.loadError')}
        action={
          <Button variant="secondary" size="sm" onClick={() => void sites.refetch()}>
            {t('app.retry')}
          </Button>
        }
      >
        {t('overview.loadErrorText')}
      </Notice>
    );
  else if (sites.data.items.length === 0)
    content = (
      <>
        <FirstSteps />
        <div className="empty px-6 py-14">
          <b>{t('overview.empty')}</b>
          <span>{t('overview.emptyText')}</span>
          <LinkButton to="/sites/new" variant="primary">
            {t('overview.add')}
          </LinkButton>
        </div>
      </>
    );
  else
    content = (
      <div className="ov">
        {/* First in reading order: on tablets and phones it stands above the list (overview.css). */}
        <Situation sites={sites.data.items} />
        <div className="main">
          <FirstSteps />
          <DownNotice sites={sites.data.items.filter(isDown)} />
          <div className="ctlbar">
            <TextField
              label={t('overview.search')}
              placeholder={t('overview.searchPlaceholder')}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <SelectField
              label={t('overview.filter.label')}
              value={filter}
              onChange={(e) => setFilter(e.target.value as Filter)}
            >
              {(['all', 'problems', 'down'] as const).map((f) => (
                <option key={f} value={f}>
                  {t(`overview.filter.${f}`)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label={t('overview.sort.label')}
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
            >
              {(['priority', 'name', 'score', 'problems'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`overview.sort.${s}`)}
                </option>
              ))}
            </SelectField>
            <Segmented
              label={t('overview.view.label')}
              className=""
              value={view}
              onChange={setView}
              options={[
                ['cards', t('overview.view.cards')],
                ['list', t('overview.view.list')],
              ]}
            />
          </div>
          {shown.length === 0 ? (
            <Notice kind="info">{t('overview.noMatch')}</Notice>
          ) : view === 'list' ? (
            <SiteTable sites={shown} />
          ) : (
            <ul className="sites">
              {shown.map((site) => (
                <li key={site.id}>
                  <SiteCard site={site} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );

  return (
    <div className="page">
      <div className="page-title">
        <h1>{t('overview.title')}</h1>
        <LinkButton to="/sites/new" variant="primary">
          {t('overview.add')}
        </LinkButton>
      </div>
      {content}
    </div>
  );
}

/**
 * The sites that are down now, with the time the outage began (from the site's overview, shared
 * with its dashboard) and, for one site, the way to help (overview.html: notice error).
 */
function DownNotice({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const timeZone = useMe().data?.account.timezone;
  const overviews = useQueries({
    queries: sites.map((site) => ({
      queryKey: ['site', site.id, 'overview', locale],
      queryFn: () =>
        unwrap(
          api.GET('/sites/{siteId}/overview', {
            params: { path: { siteId: site.id }, query: { locale } },
          }),
        ),
      refetchInterval: 60_000,
    })),
  });
  if (sites.length === 0) return null;
  const since = (iso: string) =>
    sameDay(new Date(iso), new Date(), timeZone)
      ? formatTime(iso, i18n.language, timeZone)
      : formatDateTime(iso, i18n.language, timeZone);
  const only = sites.length === 1 ? sites[0] : undefined;
  return (
    <Notice
      kind="error"
      title={t('banner.openIncidents', { count: sites.length })}
      action={
        only ? (
          <LinkButton to={`/sites/${only.id}/help`} variant="ghost" size="sm">
            {t('overview.help')}
          </LinkButton>
        ) : undefined
      }
    >
      {sites.map((site, i) => {
        const started = overviews[i]?.data?.openIncident?.startedAt;
        return (
          <span key={site.id} className="block">
            {started
              ? `${site.host} · ${t('overview.downSince', { time: since(started) })}`
              : site.host}
          </span>
        );
      })}
    </Notice>
  );
}

/** "geprüft vor 48 Sek." or not checked yet. */
function useChecked() {
  const { t, i18n } = useTranslation();
  return (site: Site) =>
    site.lastCheckAt ? formatAgo(site.lastCheckAt, i18n.language) : t('site.notChecked');
}

/** One row per site: the same facts as the card, without charts (T-79, overview.html: list). */
function SiteTable({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const checked = useChecked();
  const none = <span className="text-fg-3">–</span>;
  return (
    <TableBox label={t('overview.title')}>
      <table>
        <thead>
          <tr>
            <th scope="col">{t('overview.table.site')}</th>
            <th scope="col">{t('overview.table.status')}</th>
            <th scope="col">{t('overview.pulse.title')}</th>
            <th scope="col">{t('overview.table.uptime30d')}</th>
            <th scope="col">{t('overview.table.problems')}</th>
            <th scope="col">{t('overview.table.ssl')}</th>
            <th scope="col">{t('overview.card.response')}</th>
            <th scope="col">{t('overview.table.lastCheck')}</th>
            <th scope="col">
              <span className="sr-only">{t('overview.table.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => {
            const paused = site.status === 'paused';
            const pulse = paused ? null : hoursUptime(site.uptimeHours);
            const hot = urgent(site);
            return (
              <tr key={site.id}>
                <td>
                  <div className="flex items-center gap-2.5">
                    <ScoreRing score={site.healthScore} grade={site.healthGrade} size="xs" />
                    <div className="min-w-0">
                      <Link to={`/sites/${site.id}`} className="nm">
                        {site.name}
                      </Link>
                      <br />
                      <span className="spec">{site.host}</span>
                    </div>
                  </div>
                </td>
                <td>
                  <StatusBadge state={stateOf(site)} />
                </td>
                <td className="num">
                  {pulse === null ? none : `${formatNumber(pulse, i18n.language, 2)} %`}
                </td>
                <td className="num">
                  {site.uptime30d === null
                    ? none
                    : `${formatNumber(site.uptime30d, i18n.language, 2)} %`}
                </td>
                <td className="num">
                  <Link to={`/sites/${site.id}/findings`} className="probs">
                    <span className={hot > 0 ? 'hot' : undefined}>{hot}</span>{' '}
                    <span className="sr-only">
                      {t('overview.table.urgentWord', { count: hot })}
                    </span>{' '}
                    / {openTotal(site)}{' '}
                    <span className="sr-only">{t('overview.table.totalWord')}</span>
                  </Link>
                </td>
                <td
                  className={`num${site.sslDaysLeft !== null && site.sslDaysLeft < SSL_WARN_DAYS ? ' warn' : ''}`}
                >
                  {site.sslDaysLeft === null
                    ? none
                    : t('overview.card.days', { count: site.sslDaysLeft })}
                </td>
                <td
                  className={`num${site.responseMs !== null && site.responseMs > SLOW_MS ? ' warn' : ''}`}
                >
                  {site.responseMs === null ? none : `${site.responseMs} ms`}
                </td>
                <td className="num">{checked(site)}</td>
                <td>
                  <LinkButton
                    to={`/sites/${site.id}`}
                    variant="secondary"
                    size="sm"
                    aria-label={t('overview.open', { name: site.name })}
                  >
                    {t('overview.card.details')}
                  </LinkButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableBox>
  );
}

type Group = 'up' | 'disturbed' | 'down' | 'paused' | 'unknown';
const GROUPS: readonly Group[] = ['up', 'disturbed', 'down', 'paused', 'unknown'];
const GROUP_COLOR: Record<Group, string> = {
  up: 'var(--ok)',
  disturbed: 'var(--warn)',
  down: 'var(--bad)',
  paused: 'var(--neutral)',
  unknown: 'var(--line-strong)',
};
const groupOf = (s: Site): Group =>
  s.status === 'paused'
    ? 'paused'
    : s.uptimeState === 'up'
      ? 'up'
      : s.uptimeState === 'down'
        ? 'down'
        : s.uptimeState === 'degraded' || s.uptimeState === 'suspect'
          ? 'disturbed'
          : 'unknown';

/**
 * The situation of all sites at a glance (T-88, overview.html: "Lagebild jetzt" and "Letzte 24
 * Stunden"): a ring by state with the sites that are down, and the last 24 hours. On wide screens
 * it stands right of the list, on narrow ones above it.
 */
function Situation({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const counts = Object.fromEntries(
    GROUPS.map((g) => [g, sites.filter((s) => groupOf(s) === g).length]),
  ) as Record<Group, number>;
  const down = sites.filter((s) => groupOf(s) === 'down');
  const active = sites.filter((s) => s.status !== 'paused');
  const uptime = hoursUptime(active.flatMap((s) => s.uptimeHours));
  const withOutage = active.filter((s) => s.uptimeHours.some((h) => h.down)).length;
  const scored = sites.filter((s) => s.healthScore !== null);
  const avgScore = scored.length
    ? Math.round(scored.reduce((n, s) => n + (s.healthScore ?? 0), 0) / scored.length)
    : null;
  const urgentCount = sites.reduce((n, s) => n + urgent(s), 0);
  // Ring segments on a circle of radius 40, starting at the top.
  const length = 2 * Math.PI * 40;
  let start = 0;
  const segments = GROUPS.filter((g) => counts[g] > 0).map((g) => {
    const share = counts[g] / sites.length;
    const segment = { g, share, start };
    start += share;
    return segment;
  });
  const figures: [string, string, boolean][] = [
    [
      t('overview.situation.uptime24h'),
      uptime === null ? '–' : `${formatNumber(uptime, i18n.language, 2)} %`,
      false,
    ],
    [t('overview.situation.outages24h'), String(withOutage), withOutage > 0],
    [t('overview.summary.score'), avgScore === null ? '–' : String(avgScore), false],
    [t('overview.summary.urgent'), String(urgentCount), urgentCount > 0],
  ];
  return (
    <aside aria-label={t('overview.situation.label')}>
      <section className="lage" aria-labelledby="lage-now">
        <h2 id="lage-now">{t('overview.situation.now')}</h2>
        <div className="groups">
          <svg
            viewBox="0 0 100 100"
            role="img"
            aria-label={t('overview.situation.ringSr', {
              count: sites.length,
              up: counts.up,
              down: counts.down,
            })}
          >
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="var(--line-strong)"
              strokeWidth="10"
            />
            {segments.map(({ g, share, start: from }) => (
              <circle
                key={g}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={GROUP_COLOR[g]}
                strokeWidth="10"
                strokeDasharray={`${share * length} ${length}`}
                transform={`rotate(${from * 360 - 90} 50 50)`}
              />
            ))}
          </svg>
          <ul>
            {GROUPS.map((g) => (
              <li key={g}>
                <span>
                  <i aria-hidden="true" style={{ background: GROUP_COLOR[g] }} />
                  {t(`overview.situation.groups.${g}`)}
                </span>
                <b>{counts[g]}</b>
              </li>
            ))}
          </ul>
        </div>
        <p className="sr-only">
          {t('overview.summary.online', { count: counts.up, total: active.length })}
        </p>
        {down.length > 0 ? (
          <ul className="down">
            {down.map((s) => (
              <li key={s.id}>
                <Link to={`/sites/${s.id}`}>
                  {t('overview.situation.isDown', { name: s.host })}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
      <section className="lage" aria-labelledby="lage-24h">
        <h2 id="lage-24h">{t('overview.situation.last24h')}</h2>
        <div className="stats">
          {figures.map(([label, value, bad]) => (
            <div key={label} className="stat">
              <span className="k">{label}</span>
              <span className={bad ? 'v text-bad' : 'v'}>{value}</span>
            </div>
          ))}
        </div>
      </section>
    </aside>
  );
}

/** A site as the card of overview.html: state, last 24 hours, score, three figures, trends. */
function SiteCard({ site }: { site: Site }) {
  const { t, i18n } = useTranslation();
  const checked = useChecked();
  const state = stateOf(site);
  const paused = state === 'paused';
  const trend = site.healthTrend;
  const delta =
    trend.length >= 2 ? (trend[trend.length - 1]?.score ?? 0) - (trend[0]?.score ?? 0) : null;
  const h = site.hosting;
  const total = openTotal(site);
  const hot = urgent(site);
  const pulse = hoursUptime(site.uptimeHours);
  const sslWarn = site.sslDaysLeft !== null && site.sslDaysLeft < SSL_WARN_DAYS;
  const slow = site.responseMs !== null && site.responseMs > SLOW_MS;
  const kpis: [string, ReactNode, boolean][] = [
    [
      t('overview.card.uptimeShort'),
      site.uptime30d === null ? '–' : `${formatNumber(site.uptime30d, i18n.language, 2)} %`,
      false,
    ],
    [t('overview.card.response'), site.responseMs === null ? '–' : `${site.responseMs} ms`, slow],
    [
      t('overview.card.sslLeft'),
      site.sslDaysLeft === null ? '–' : t('overview.card.days', { count: site.sslDaysLeft }),
      sslWarn,
    ],
  ];
  const grade = site.healthGrade;
  return (
    <article className={paused ? 'site paused' : 'site'}>
      <div className="hd">
        <div className="min-w-0">
          <h2>
            <Link to={`/sites/${site.id}`}>{site.name}</Link>
          </h2>
          <span className="host">{site.host}</span>
          {site.cmsDetected ? <span className="cms">{site.cmsDetected}</span> : null}
        </div>
        <div className="right">
          {site.healthScore !== null && grade && isGrade(grade) ? (
            <GradePill grade={grade} score={site.healthScore} />
          ) : null}
          <StatusBadge state={state} />
        </div>
      </div>

      <div className="h24">
        <PulseStrip hours={paused ? [] : site.uptimeHours} bars />
        <div className="cap">
          <span>{t('overview.pulse.title')}</span>
          <span>
            {paused
              ? t('overview.card.pausedCap')
              : pulse === null
                ? t('overview.noData')
                : t('overview.pulse.share', { pct: formatNumber(pulse, i18n.language, 2) })}
          </span>
        </div>
      </div>

      {state === 'down' ? <p className="warn-line">{t('overview.card.downNow')}</p> : null}

      <div className="score">
        <span
          role="img"
          aria-label={
            site.healthScore === null || !grade
              ? t('overview.card.noScore')
              : t('overview.card.scoreSr', { score: site.healthScore, grade })
          }
        >
          {site.healthScore === null ? (
            <span className="none">{t('overview.card.noScore')}</span>
          ) : (
            <span className="big">
              {site.healthScore}
              <small>/ 100</small>
            </span>
          )}
        </span>
        {paused ? (
          site.lastCheckAt ? (
            <span className="d">
              {t('overview.card.asOf', { ago: formatAgo(site.lastCheckAt, i18n.language) })}
            </span>
          ) : null
        ) : delta !== null ? (
          <span className={delta > 0 ? 'd up' : delta < 0 ? 'd down' : 'd'}>
            {delta === 0
              ? t('overview.card.deltaSame')
              : t(delta > 0 ? 'overview.card.deltaUp' : 'overview.card.deltaDown', {
                  points: Math.abs(delta),
                })}
          </span>
        ) : null}
      </div>

      <dl className="m3">
        {kpis.map(([label, value, warn]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd className={warn ? 'warn' : undefined}>{value}</dd>
          </div>
        ))}
      </dl>

      <HealthTrend points={trend} compact muted={paused} />
      <UptimeStrip days={site.uptimeDays} today={new Date()} compact />

      <div className="probs">
        <div className="row">
          <span>
            {total === 0
              ? t('overview.noProblems')
              : t('overview.card.openCount', { count: total })}
          </span>
          {hot > 0 ? (
            <Link to={`/sites/${site.id}/findings`}>
              {t('overview.table.urgent', { count: hot })} →
            </Link>
          ) : total > 0 ? (
            <span className="calm">{t('overview.table.urgent', { count: 0 })}</span>
          ) : null}
        </div>
        <SeverityBar siteId={site.id} counts={site.openFindings} order={SEVERITIES} bare />
      </div>

      <div className="ft">
        <span>
          {site.lastCheckAt ? t('overview.card.checked', { ago: checked(site) }) : checked(site)}
          {h?.country ? (
            <span title={`IP ${h.ip}`}>
              {' · '}
              <span aria-hidden="true">{flagOf(h.country)}</span>
              <span className="sr-only">{regionName(h.country, i18n.language)}</span>
            </span>
          ) : null}
          {h?.org ? (
            <>
              {' '}
              <span title={h.asn ? `AS${h.asn}` : undefined}>{h.org}</span>
            </>
          ) : null}
          {h?.cdn ? ` · ${t('overview.card.viaCdn', { cdn: h.cdn })}` : null}
          {site.domainDaysLeft !== null && site.domainDaysLeft < DOMAIN_WARN_DAYS ? (
            <span className="soon">
              {' · '}
              {t('overview.card.domain')}: {t('overview.card.days', { count: site.domainDaysLeft })}
            </span>
          ) : null}
        </span>
        <div className="acts">
          <LinkButton to={`/sites/${site.id}/findings`} variant="secondary" size="sm">
            {t('overview.card.findings')}
          </LinkButton>
          <LinkButton
            to={`/sites/${site.id}`}
            variant="primary"
            size="sm"
            aria-label={t('overview.open', { name: site.name })}
          >
            {t('overview.card.details')}
          </LinkButton>
        </div>
      </div>
    </article>
  );
}
